<template>
  <div class="flex size-full flex-col">
    <!-- Banner Messages -->
    <template
      v-for="(message, index) in messages"
      :key="message"
    >
      <lokal-banner-message-dialog
        v-if="message[1]"
        v-model="showBanners[index]"
        :show-dialog="message[0].includes('dialog')"
        :color-key="message[0] as ColorKeyType"
        :message="message[1]"
        title="Información importante"
      />
    </template>
    <lokal-banner
      v-model="showPontetialMakerContactBanner"
      :message="potentialMakerContactBannerMessage"
      :color-key="'notice' as ColorKeyType"
    />
    <div class="flex size-full flex-col items-center">
      <div class="flex size-full flex-col items-center bg-white">
        <div
          class="flex w-full flex-col-reverse items-center justify-center p-4 sm:flex-col md:px-8 md:py-16 lg:flex-row lg:px-12 xl:px-16"
        >
          <div
            class="mt-8 flex h-full flex-col items-center justify-center text-center md:mt-0 lg:mr-6 lg:w-3/5 lg:items-start lg:text-left xl:mr-12 xl:w-auto"
          >
            <h1 class="text-4xl font-medium text-gray-700 md:text-6xl">
              Compra productos que tus <br />
              clientes amarán.
            </h1>
            <p class="mt-6 max-w-3xl text-xl text-gray-600 md:text-2xl">
              Ya sea que tengas un emporio, una farmacia o una tienda saludable, encuentra todos los productos que
              buscas en Lokal.
            </p>
            <p class="mt-2 text-lg text-gray-600">
              Encuentra el
              <span class="inline-block animate-pulse font-semibold text-lk-green">producto perfecto</span>
              para tu tienda.
            </p>
            <div class="mt-8 flex w-full items-center justify-center gap-2 lg:justify-start">
              <a
                v-if="sessionStore.user?.id"
                class="rounded-md bg-lk-green px-4 py-2 text-base font-semibold text-white transition-colors hover:bg-lk-green-deep md:px-8 md:py-4 md:text-lg"
                href="/makers"
              >
                <span class="hidden md:inline">Registrarse para comprar</span>
                <span class="inline md:hidden">Quiero comprar</span>
              </a>
              <button
                v-else
                class="rounded-md bg-lk-green px-4 py-2 text-base font-semibold text-white transition-colors hover:bg-lk-green-deep md:px-8 md:py-4 md:text-lg"
                @click="openRegisterDialog"
              >
                <span class="hidden md:inline">Registrarse para comprar</span>
                <span class="inline md:hidden">Quiero comprar</span>
              </button>
              <a
                class="rounded-md border border-lk-green bg-white px-4 py-2 text-base font-semibold text-lk-green transition-colors hover:border-lk-green-deep hover:bg-gray-50 hover:text-lk-green-deep md:px-8 md:py-4 md:text-lg"
                href="/potential_maker"
              >
                <span class="hidden md:inline">Quiero vender en Lokal</span>
                <span class="inline md:hidden">Quiero vender</span>
              </a>
            </div>
          </div>
          <div
            class="mt-0 grid grid-cols-2 place-items-center gap-2 sm:mt-8 sm:grid-cols-3 md:gap-4 lg:mt-0 lg:grid-cols-2 2xl:grid-cols-3"
          >
            <div
              v-for="category in slicedCategories"
              :key="category.id"
              class="group cursor-pointer"
            >
              <a
                :href="`/makers?category_ids=${category.id}`"
                class="relative block"
              >
                <div class="aspect-square h-36 overflow-hidden rounded-md bg-gray-100 md:h-40 md:rounded-lg">
                  <div
                    class="size-full bg-cover bg-center transition-transform group-hover:scale-105"
                    :style="{ 'background-image': `url(${category.image?.derivatives?.jpgMd?.url})` }"
                  />
                </div>
                <p
                  class="inset-x-0 bottom-0 hidden bg-white/80 p-1 text-center text-xs font-medium text-gray-700 md:absolute md:p-2 md:text-sm group-hover:md:block"
                >
                  {{ category.name }}
                </p>
              </a>
            </div>
          </div>
        </div>
      </div>
      <the-landing-best-makers-by-categories :categories="categories" />
      <the-landing-benefits />
      <div class="flex w-full flex-col items-center bg-white px-4 py-16 md:px-8 lg:px-16">
        <div class="w-full max-w-6xl text-center">
          <h2 class="mb-6 text-2xl font-medium text-stone-700 md:text-3xl xl:text-4xl">
            La mejor selección de marcas para tu tienda, todo en un lugar
          </h2>
          <p class="mb-12 text-lg text-stone-500 md:text-xl xl:text-2xl">Más de 400 marcas esperándote</p>
          <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div class="rounded-lg bg-stone-50 p-4">
              <p class="mb-2 text-2xl font-semibold text-stone-700 md:text-3xl xl:text-4xl">400+</p>
              <p class="text-sm text-stone-600 md:text-base xl:text-lg">Marcas verificadas</p>
            </div>
            <div class="rounded-lg bg-stone-50 p-4">
              <p class="mb-2 text-2xl font-semibold text-stone-700 md:text-3xl xl:text-4xl">7000+</p>
              <p class="text-sm text-stone-600 md:text-base xl:text-lg">Productos únicos</p>
            </div>
            <div class="rounded-lg bg-stone-50 p-4">
              <p class="mb-2 text-2xl font-semibold text-stone-700 md:text-3xl xl:text-4xl">60 días</p>
              <p class="text-sm text-stone-600 md:text-base xl:text-lg">Términos de pago</p>
            </div>
          </div>
        </div>
      </div>
      <the-landing-testimonials :testimonials="testimonials" />
      <the-landing-buying-steps />
      <div class="flex w-full flex-col items-center bg-lk-green-deep px-4 py-16 md:px-8 lg:px-16">
        <div class="w-full max-w-4xl text-center">
          <h2 class="mb-6 text-2xl font-medium text-white md:text-3xl xl:text-4xl">Compra para tu tienda</h2>
          <p class="mb-8 text-lg text-white opacity-90 md:text-xl xl:text-2xl">
            Regístrate para desbloquear precios mayoristas con más de 400 marcas.
          </p>
          <a
            v-if="sessionStore.user?.id"
            class="rounded-md bg-white px-4 py-2 text-base font-semibold text-lk-green-deep transition-colors hover:bg-gray-100 md:px-8 md:py-4 md:text-lg xl:text-xl"
            href="/makers"
          >
            Registrarse para comprar
          </a>
          <button
            v-else
            class="rounded-md bg-white px-4 py-2 text-base font-semibold text-lk-green-deep transition-colors hover:bg-gray-100 md:px-8 md:py-4 md:text-lg xl:text-xl"
            @click="openRegisterDialog"
          >
            Registrarse para comprar
          </button>
        </div>
      </div>
      <the-landing-q-and-a
        v-if="frequentQuestions.length > 0"
        id="Q&A"
        :frequent-questions="frequentQuestions"
      />
      <PotentialMakerContactDialog
        v-model="showPotentialMakerContactDialog"
        @pontential-maker-contact-created="displayPotentialMakerContactBanner"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, inject, type Ref } from 'vue';
import useGeneralMenuStore from 'stores/general-menu-store';
import useSessionStore from 'stores/user-store';
import LokalBanner, { type ColorKeyType } from './shared/lokal-banner.vue';
import PotentialMakerContactDialog from './potential-maker-contact-dialog.vue';
import theLandingQAndA from './the-landing-q-and-a.vue';
import theLandingBestMakersByCategories from './the-landing-best-makers-by-categories.vue';
import theLandingTestimonials from './the-landing-testimonials.vue';
import theLandingBenefits from './the-landing-benefits.vue';
import theLandingBuyingSteps from './the-landing-buying-steps.vue';

interface LandingProps {
  testimonials: Testimonial[];
  categories: Category[];
  frequentQuestions: FrequentQuestion[];
  messages?: string[][];
}

const props = withDefaults(defineProps<LandingProps>(), {
  messages: () => [['', '']],
});

const sessionStore = useSessionStore();

const showBanners = ref({} as { [string: number]: boolean });
props.messages.forEach((_, index) => {
  showBanners.value[index] = true;
});

const showPotentialMakerContactDialog = ref(false);
const showPontetialMakerContactBanner = ref(false);
const potentialMakerContactBannerMessage = ref('');

const isMobile = inject('isMobile') as Ref<boolean>;
const slicedCategories = computed(() => (isMobile.value ? props.categories.slice(0, 2) : props.categories.slice(0, 6)));

function displayPotentialMakerContactBanner() {
  potentialMakerContactBannerMessage.value = 'Se ha creado correctamente la solicitud.';
  showPontetialMakerContactBanner.value = true;
}

const menuStore = useGeneralMenuStore();
function openRegisterDialog() {
  menuStore.showUserRegisterDialog = true;
  menuStore.initialValueSignIn = false;
}
</script>
